export type Tour = {
  img: string;
  title: string;
  desc: string;
  price: string;
  popular?: {
    subtitle: string;
    description: string;
  };
  region?: string;
};

export type RegionData = {
  map: string;
  categories: Record<string, Tour[]>;
};

export type CategoryMeta = {
  duration: string;
  distance: string;
  bestTime: string;
  level: string;
  price: string;
};

export type PopularTourSlide = {
  img: string;
  title: string;
  place: string;
  subtitle: string;
  description: string;
};

export type ItineraryImages = {
  heroImage: string;
  mapImage: string;
  highlightImage: string;
  galleryImages: string[];
  ctaImage: string;
};

export const navItems = [
  "Private Tours",
  "Festival Tours",
  "Trekking & Hiking Tours",
  "Camping & Outdoor Adventure",
  "Exploring the Highlands"];

export const regions = ["All Regions", "Paro", "Thimphu", "Punakha"];

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const categoryMeta: Record<string, CategoryMeta> = {
  "Private Tours": {
    duration: "4 nights / 5 days",
    distance: "Flexible",
    bestTime: "Year-round",
    level: "Easy",
    price: "from Nu. 2,400 per person"},
  "Festival Tours": {
    duration: "5 nights / 6 days",
    distance: "Flexible",
    bestTime: "Mar - Apr or Sep - Nov",
    level: "Easy",
    price: "from Nu. 2,650 per person"},
  "Trekking & Hiking Tours": {
    duration: "7 nights / 8 days",
    distance: "45 - 60 km",
    bestTime: "Mar - May and Sep - Dec",
    level: "Moderate",
    price: "from Nu. 2,900 per person"},
  "Camping & Outdoor Adventure": {
    duration: "5 nights / 6 days",
    distance: "20 - 35 km",
    bestTime: "Apr - Jun and Sep - Oct",
    level: "Moderate",
    price: "from Nu. 2,500 per person"},
  "Exploring the Highlands": {
    duration: "6 nights / 7 days",
    distance: "Flexible",
    bestTime: "Jun - Sep",
    level: "Moderate",
    price: "from Nu. 2,800 per person"}};

export const regionStops: Record<string, string[]> = {
  Paro: ["Paro", "Tiger's Nest", "Chele La", "Drugyel Dzong", "Paro Valley"],
  Thimphu: ["Thimphu", "Buddha Dordenma", "Tango", "Dochula", "Wang Chhu"],
  Punakha: [
    "Punakha",
    "Punakha Dzong",
    "Chimi Lhakhang",
    "Mo Chhu",
    "Pho Chhu"]};

const baseRegionData: Record<string, RegionData> = {
  Paro: {
    map: "/img/maps/paro_single_map.svg",
    categories: {
      "Private Tours": [],
      "Festival Tours": [
        {
          img: "/img/tours/paro_festival.jpg",
          title: "Paro Festival & Valley",
          desc: "5 days centered on Paro Tsechu with dzongs, markets, and valley walks.",
          price: "from Nu. 3,900 per person",
          popular: {
            subtitle:
              "5 DAYS OF PARO TSECHU, DZONG VISITS, AND VALLEY CULTURE.",
            description:
              "Experience Bhutan's signature festival with curated viewing, heritage stops, and artisan encounters across Paro.",
          },
        },
      ],
      "Trekking & Hiking Tours": [
        {
          img: "/img/tours/druk_path.jpg",
          title: "Druk Path Trek",
          desc: "6 days of alpine lakes and ridgelines linking Paro to Thimphu.",
          price: "from Nu. 4,200 per person",
          popular: {
            subtitle:
              "6 DAYS OF HIGH RIDGES AND LAKES BETWEEN PARO AND THIMPHU.",
            description:
              "A classic trek with sweeping Himalayan views, high camps, and rewarding passes.",
          },
        },
        {
          img: "/img/tours/jomolhari.jpg",
          title: "Jhomolhari Base Camp Trek",
          desc: "8 days of high-altitude trekking with Jhomolhari views and base camp nights.",
          price: "from Nu. 5,600 per person",
        },
      ],
      "Camping & Outdoor Adventure": [
        {
          img: "/img/tours/bumdrak.jpg",
          title: "Bumdra Ridge Camp",
          desc: "3 days of cliffside camping with panoramic Himalayan skies.",
          price: "from Nu. 2,600 per person",
        },
      ],
      "Exploring the Highlands": [],
    },
  },
  Thimphu: {
    map: "/img/maps/thimphu_single_map.svg",
    categories: {
      "Private Tours": [
      ],
      "Festival Tours": [
        {
          img: "/img/tours/thimphu_festival.png",
          title: "Thimphu Tsechu Highlights",
          desc: "5 days focused on Thimphu's biggest festival and city culture.",
          price: "from Nu. 3,850 per person"}],
      "Trekking & Hiking Tours": [
        {
          img: "/img/tours/phajoding.png",
          title: "Thimphu Monastery Hikes",
          desc: "3 days combining Phajoding, Tango, and Cheri trails.",
          price: "from Nu. 2,450 per person"}],
      "Camping & Outdoor Adventure": [
        {
          img: "/img/tours/thimphu_dungtsho.png",
          title: "Dungtsho Lake Camp",
          desc: "3 days of lakeside camping, pine walks, and sunrise views.",
          price: "from Nu. 2,700 per person"}],
      "Exploring the Highlands": [
        {
          img: "/img/tours/thimphu_dochula.jpg",
          title: "Dochula Highlands Loop",
          desc: "3 days of high pass viewpoints, temple visits, and ridge walks.",
          price: "from Nu. 3,100 per person"}]}},
  Punakha: {
    map: "/img/maps/punakha_single_map.svg",
    categories: {
      "Private Tours": [
        {
          img: "/img/tours/punakha_dzong.jpg",
          title: "Punakha Sacred Valley",
          desc: "3 days of dzong visits, rice-field walks, and blessings.",
          price: "from Nu. 2,400 per person"}],
      "Festival Tours": [
        {
          img: "/img/tours/punakha_festival.png",
          title: "Punakha Festival Journey",
          desc: "5 days around Punakha's Drubchen and Tsechu celebrations.",
          price: "from Nu. 3,600 per person"}],
      "Trekking & Hiking Tours": [
      ],
      "Camping & Outdoor Adventure": [
        {
          img: "/img/tours/punakha_walk.jpg",
          title: "Punakha Riverside Camp",
          desc: "3 days of rafting, riverside camping, and valley strolls.",
          price: "from Nu. 2,950 per person"}],
      "Exploring the Highlands": [
        {
          img: "/img/tours/gasa_highland.png",
          title: "Gasa Highlands Escape",
          desc: "5 days of hot springs, ridge villages, and alpine drives.",
          price: "from Nu. 3,850 per person"}]}}};

const buildAllRegions = (data: Record<string, RegionData>): RegionData => {
  const categories: Record<string, Tour[]> = {};
  for (const [regionName, region] of Object.entries(data)) {
    for (const [cat, tours] of Object.entries(region.categories)) {
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(
        ...tours.map((tour) => ({
          ...tour,
          region: regionName}))
      );
    }
  }
  return {
    map: "/img/maps/bhutan_overview.svg",
    categories};
};

export const regionData: Record<string, RegionData> = {
  ...baseRegionData,
  "All Regions": buildAllRegions(baseRegionData)};

const buildItineraryImages = (slug: string): ItineraryImages => ({
  heroImage: `/img/itineraries/${slug}/hero.jpg`,
  mapImage: `/img/itineraries/${slug}/map.svg`,
  highlightImage: `/img/itineraries/${slug}/highlight.jpg`,
  galleryImages: [
    `/img/itineraries/${slug}/gallery-1.png`,
    `/img/itineraries/${slug}/gallery-2.png`,
    `/img/itineraries/${slug}/gallery-3.png`,
    `/img/itineraries/${slug}/gallery-4.png`,
    `/img/itineraries/${slug}/gallery-5.png`],
  ctaImage: `/img/itineraries/${slug}/cta.jpg`});

const buildItineraryImageIndex = (
  data: Record<string, RegionData>
): Record<string, ItineraryImages> => {
  const index: Record<string, ItineraryImages> = {};
  for (const [regionName, region] of Object.entries(data)) {
    for (const tours of Object.values(region.categories)) {
      tours.forEach((tour) => {
        const slug = slugify(`${tour.title}-${regionName}`);
        index[slug] = buildItineraryImages(slug);
      });
    }
  }
  return index;
};

export const itineraryImagesBySlug = buildItineraryImageIndex(baseRegionData);

export const getItineraryImages = (slug: string) =>
  itineraryImagesBySlug[slug.toLowerCase()] ?? buildItineraryImages(slug);

const itineraryTemplates: Record<string, string[]> = {
  "Festival Tours": [
    "Arrival and festival briefing.",
    "Heritage walk and craft market visit.",
    "Festival day with mask dances and rituals.",
    "Monastery visit and scenic viewpoints.",
    "Village immersion and local lunch.",
    "Leisure morning and farewell transfer."],
  "Trekking & Hiking Tours": [
    "Arrival and acclimatization.",
    "Trail briefing and warm-up hike.",
    "Full trek day with ridge views.",
    "High camp, sunrise panorama, and alpine breakfast.",
    "Descent through forest toward the valley.",
    "Return to town and farewell dinner."],
  "Camping & Outdoor Adventure": [
    "Arrival and camp briefing.",
    "Drive to the trailhead and set camp.",
    "Day hike with picnic views.",
    "Bonfire evening, stargazing, and warm camp dinner.",
    "Sunrise walk and scenic breaks.",
    "Leisure morning and return transfer."],
  "Exploring the Highlands": [
    "Arrival and village orientation.",
    "Highland drive with ridge viewpoints.",
    "Yak herder visit and pasture walk.",
    "Monastery stop with storytelling.",
    "Return via scenic ridge roads and farewell dinner."],
  "Private Tours": [
    "Arrival and tailored briefing.",
    "Guided culture walk to a signature landmark.",
    "Scenic drive via a high pass and viewpoints.",
    "Heritage visit with local stories.",
    "Leisure day with artisan visits and farewell meal."]};

export const tourStepOverrides: Record<string, string[]> = {
  "Paro Festival & Valley": [
    "Arrival in Paro and festival orientation.",
    "Dzong visit and cultural walk.",
    "Full festival day with mask dances.",
    "Market stroll and artisan encounters.",
    "Farewell breakfast and departure."],
  "Druk Path Trek": [
    "Arrival in Paro and trek preparation briefing.",
    "Trek to Jangchulakha camp via ridgeline views.",
    "Hike to Jimilangtsho with lakeside camp.",
    "Walk to Simkota pass and descent toward Thimphu.",
    "City arrival, celebratory dinner, and rest.",
    "Departure transfer."],
  "Jhomolhari Base Camp Trek": [
    "Arrival in Paro and gear check.",
    "Drive to Shana and trek to first camp.",
    "Trek to Soi Thang with river views.",
    "Ascent to Jangothang base camp.",
    "Acclimatization hike with Jhomolhari views.",
    "Trek to Soi Yaksa via high pass.",
    "Return trek and farewell dinner.",
    "Departure transfer."],
  "Bumdra Ridge Camp": [
    "Arrival in Paro and trek briefing.",
    "Drive to trailhead and hike to camp.",
    "Sunrise hike and return to Paro."],
  "Thimphu Tsechu Highlights": [
    "Arrival in Thimphu and festival orientation.",
    "Dzong visit and ritual preparations.",
    "Full festival day with mask dances.",
    "City craft market and cultural walk.",
    "Leisure morning and departure."],
  "Royal Dance Heritage": [
    "Arrival in Thimphu and welcome briefing.",
    "Museum visit and royal heritage walk.",
    "Festival day with traditional dances.",
    "Departure transfer."],
  "Thimphu Monastery Hikes": [
    "Arrival in Thimphu and trail briefing.",
    "Phajoding monastery hike and picnic.",
    "Tango and Cheri trails with departure."],
  "Dungtsho Lake Camp": [
    "Arrival in Thimphu and drive to Motithand base and start the climb",
    "Hike through Phajoding and Thujidrak to Dungtsho",
    "Lakeside camp setup and forest walk.",
    "Sunrise by the lake and return to town."],
  "Dochula Highlands Loop": [
    "Arrival in Thimphu and highland briefing.",
    "Drive to Dochula and hike to Lungchutse.",
    "Temple visit and return to town."],
  "Punakha Sacred Valley": [
    "Arrival in Punakha and welcome briefing.",
    "Private walk through Punakha Dzong.",
    "Chimi Lhakhang blessing and departure."],
  "Punakha Festival Journey": [
    "Arrival in Punakha and festival orientation.",
    "Dzong visit and ritual rehearsal.",
    "Festival day with warrior dances.",
    "Village walk and artisan visits.",
    "Departure transfer."],
  "Punakha Riverside Camp": [
    "Arrival in Punakha and rafting briefing.",
    "Rafting on Pho Chhu with camp setup.",
    "Riverside morning walk and return."],
  "Gasa Highlands Escape": [
    "Arrival in Punakha and early departure.",
    "Drive to Gasa valley with stops.",
    "Soak in hot springs and picnic.",
    "Ridge village visit and rest.",
    "Scenic return drive and departure."]};

export const buildSteps = (category: string, region: string, title: string) => {
  const stops = regionStops[region] ?? [
    "Bhutan",
    "Himalayan ridges",
    "Ancient monasteries"];
  const templates = itineraryTemplates[category];
  if (!templates) return itineraryTemplates["Private Tours"];
  return templates.map((step) =>
    step
      .replace("Arrival", `Arrival in ${stops[0]}`)
      .replace("trailhead", `trailhead near ${stops[1]}`)
  );
};

export const getTourSteps = (category: string, region: string, title: string) =>
  tourStepOverrides[title] ?? buildSteps(category, region, title);

const buildPopularTourSlides = (
  data: Record<string, RegionData>
): PopularTourSlide[] => {
  const slides: PopularTourSlide[] = [];
  for (const [regionName, region] of Object.entries(data)) {
    for (const tours of Object.values(region.categories)) {
      tours.forEach((tour) => {
        if (!tour.popular) return;
        slides.push({
          img: tour.img,
          title: tour.title,
          place: regionName,
          subtitle: tour.popular.subtitle,
          description: tour.popular.description});
      });
    }
  }
  return slides;
};

export const popularTourSlides = buildPopularTourSlides(baseRegionData);
